// externals
import type { Reducer } from 'redux';

// libraries
import type { CreditAppState } from '@makemydeal/dr-dash-types';
import { initActionTypes } from '@makemydeal/dr-shared-store';
import { SOCKET_EVENT_RECEIVED } from '../actionTypes/socketActionTypes';
import { SAVE_OFFER_AFTER_CANCEL } from '../actionTypes/offerActionTypes';
import { CREDIT_APP_SEARCH_MERGE_DEAL } from '../actionTypes/creditAppSearchActionTypes';
import { GET_CREDIT_APP_DATA_SUCCESS } from '../actionTypes/creditDecisionActionTypes';
import { CONTRACTING_STAGE_COPY_SUCCESS } from '../actionTypes/contractingActionTypes';
import {
    PARTICIPANTS_SAVE_STARTED,
    PARTICIPANTS_SAVE_FINISHED_WITHOUT_COPY,
    CREDIT_APP_COPIED_BY_PARTICIPANTS_SAVE,
    CREDIT_APP_UPDATED_ALERT_DISMISSED
} from '../actionTypes/creditAppActionTypes';

const { INIT_PENCIL_SUCCESS } = initActionTypes;

export const creditAppInitialState: CreditAppState = {
    creditAppReferenceId: '',
    accelerateCreditAppId: '',
    coApplicantEmail: '',
    status: '',
    submittedOn: 0
};

export type CreditAppReducer = Reducer<CreditAppState>;

export const reducer: CreditAppReducer = (state: CreditAppState = creditAppInitialState, action: any) => {
    switch (action.type) {
        case INIT_PENCIL_SUCCESS: {
            return {
                ...state,
                ...action.payload.offer.creditApp
            };
        }
        case GET_CREDIT_APP_DATA_SUCCESS: {
            return {
                ...state,
                status: action.payload.data.creditAppInfo.status || '',
                submittedOn: action.payload.data.creditAppInfo.submittedOn || 0
            };
        }
        case SOCKET_EVENT_RECEIVED: {
            const event = action.payload;

            // get the credit application ID from the WS event data
            const newCreditAppId = event.data?.dealStructure?.creditApplication?.creditApplicationId;
            const hasCreditAppIdUpdated = newCreditAppId && state.creditAppReferenceId !== newCreditAppId;

            if (newCreditAppId && hasCreditAppIdUpdated) {
                if (state.isParticipantsSaveInFlight) {
                    return { ...state, creditAppReferenceId: newCreditAppId, pendingSocketCreditAppId: newCreditAppId };
                }
                return { ...state, creditAppUpdateSource: 'external', creditAppReferenceId: newCreditAppId };
            }
            return state;
        }
        case PARTICIPANTS_SAVE_STARTED: {
            return { ...state, isParticipantsSaveInFlight: true };
        }
        case CREDIT_APP_COPIED_BY_PARTICIPANTS_SAVE: {
            const copiedCreditAppId: string = action.payload.creditApplicationId;
            const { pendingSocketCreditAppId } = state;
            const settled = { ...state, isParticipantsSaveInFlight: false, pendingSocketCreditAppId: undefined };

            if (pendingSocketCreditAppId && pendingSocketCreditAppId !== copiedCreditAppId) {
                return { ...settled, creditAppUpdateSource: 'external' };
            }
            return { ...settled, creditAppReferenceId: copiedCreditAppId, creditAppUpdateSource: 'participantsSave' };
        }
        case PARTICIPANTS_SAVE_FINISHED_WITHOUT_COPY: {
            return {
                ...state,
                isParticipantsSaveInFlight: false,
                pendingSocketCreditAppId: undefined,
                ...(state.pendingSocketCreditAppId && { creditAppUpdateSource: 'external' as const })
            };
        }
        case CREDIT_APP_UPDATED_ALERT_DISMISSED: {
            if (state.creditAppUpdateSource !== 'participantsSave') return state;
            return { ...state, creditAppUpdateSource: undefined };
        }

        case SAVE_OFFER_AFTER_CANCEL: {
            return {};
        }
        case CREDIT_APP_SEARCH_MERGE_DEAL: {
            const creditApplicationId = action.payload.dealStructure?.creditApplication?.creditApplicationId;
            if (creditApplicationId) {
                return {
                    ...state,
                    creditAppReferenceId: creditApplicationId,
                    status: 'Submitted'
                };
            }
            return state;
        }
        case CONTRACTING_STAGE_COPY_SUCCESS: {
            const creditAppId = action.payload?.eventKeyData?.creditAppId;
            if (!creditAppId) return state;
            return {
                ...state,
                creditAppReferenceId: creditAppId
            };
        }
        default: {
            return state;
        }
    }
};
